import { useEffect, useMemo, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import Modal from '../Modal';
import { BlockLoader } from '../loading';
import { ModalFormErrorBanner } from '../ModalFormErrorBanner';
import { preJourneyPlanAPI } from '../../services/api';
import type { PlanBaseCitiesResponse, PlanBaseCityChange, PlanBaseCityPerson } from '../../types/preJourneyPlan';

/**
 * Store Visits Configuration › Pre-Journey Plan › Review base cities.
 *
 * A person's base city decides which of their stores are inside the city and which are
 * upcountry. It is the city of their 1Office position unless one is typed here. One row per
 * person on the plan; the box shows a city typed here, and is empty (the 1Office city as its
 * placeholder) when none was. Reset empties the box, so the 1Office city applies again.
 *
 * Save sends only the rows that changed, so a city someone else typed meanwhile for another
 * person is never overwritten. The server then rebuilds this month's plan.
 */

const MAX_CITY = 100;
const LOAD_FAILED = 'Could not load the base cities.';
const SAVE_FAILED = 'Could not save the base cities.';

const apiMessage = (e: unknown, fallback: string): string => {
  const message = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

const savedCity = (p: PlanBaseCityPerson): string => (p.overrideCity ?? '').trim();

export const DISCARD_CITIES_PROMPT = 'Discard your unsaved base city changes?';

interface Props {
  projectId: number;
  onClose: () => void;
  /** After a save: the server has rebuilt this month's plan, or says why it could not (buildError). */
  onSaved: (changed: number, buildError: string | null) => void;
}

export default function PreJourneyBaseCitiesModal({ projectId, onClose, onSaved }: Props) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [data, setData] = useState<PlanBaseCitiesResponse | null>(null);
  // The box of each person as typed, by employee code.
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    preJourneyPlanAPI
      .getBaseCities(projectId)
      .then((res) => {
        if (!alive) return;
        const got = res.data?.data as PlanBaseCitiesResponse | undefined;
        if (!got || !Array.isArray(got.people)) {
          setLoadError(LOAD_FAILED);
          return;
        }
        setData(got);
        setTyped(Object.fromEntries(got.people.map((p) => [p.employeeCode, p.overrideCity ?? ''])));
      })
      .catch((e) => alive && setLoadError(apiMessage(e, LOAD_FAILED)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [projectId]);

  const changes = useMemo<PlanBaseCityChange[]>(() => (data?.people ?? []).flatMap((p) => {
    const now = (typed[p.employeeCode] ?? '').trim();
    if (now === savedCity(p)) return [];
    return [{ employeeCode: p.employeeCode, city: now === '' ? null : now }];
  }), [data, typed]);

  const save = async () => {
    if (saving || changes.length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await preJourneyPlanAPI.saveBaseCities(projectId, changes);
      const answer = res.data?.data as PlanBaseCitiesResponse | undefined;
      onSaved(changes.length, answer?.buildError ?? null);
    } catch (e) {
      setSaveError(apiMessage(e, SAVE_FAILED));
    } finally {
      setSaving(false);
    }
  };

  const people = data?.people ?? [];
  const listId = `pre-journey-city-options-${projectId}`;

  // Closing with edits not saved asks first, as leaving the plan tab does.
  const close = () => {
    if (saving) return;
    if (changes.length > 0 && !window.confirm(DISCARD_CITIES_PROMPT)) return;
    onClose();
  };

  return (
    <Modal
      isOpen
      onClose={close}
      title="Base cities"
      size="xl"
      footer={(
        <>
          <button
            type="button"
            onClick={close}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving || changes.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}
            Save
          </button>
        </>
      )}
    >
      <p className="text-sm text-gray-500">
        A store in the base city is inside the city; any other store is upcountry. Leave a box empty to use the 1Office city.
      </p>
      {saveError && <div className="mt-3"><ModalFormErrorBanner message={saveError} /></div>}

      {loading ? (
        <BlockLoader className="py-12" />
      ) : loadError ? (
        <div className="mt-3"><ModalFormErrorBanner message={loadError} /></div>
      ) : people.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">Nobody is on the plan. Tick a role and save first.</div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <datalist id={listId}>
            {(data?.cityOptions ?? []).map((c) => <option key={c} value={c} />)}
          </datalist>
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-4 py-3">Person</th>
                <th className="whitespace-nowrap px-4 py-3">Role</th>
                <th className="whitespace-nowrap px-4 py-3">1Office city</th>
                <th className="whitespace-nowrap px-4 py-3">Base city</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 bg-white">
              {people.map((p) => {
                const value = typed[p.employeeCode] ?? '';
                const name = p.name || p.employeeCode;
                return (
                  <tr key={p.employeeCode} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-gray-900">{name}</div>
                      <div className="text-xs text-gray-400">{p.employeeCode}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{p.roleName}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {p.positionCity ?? <span className="text-amber-700">None</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="text"
                          list={listId}
                          maxLength={MAX_CITY}
                          aria-label={`Base city for ${name}`}
                          placeholder={p.positionCity ?? 'Type a city'}
                          value={value}
                          disabled={saving}
                          onChange={(e) => setTyped((cur) => ({ ...cur, [p.employeeCode]: e.target.value }))}
                          className="w-48 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
                        />
                        {value.trim() !== '' && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => setTyped((cur) => ({ ...cur, [p.employeeCode]: '' }))}
                            aria-label={`Reset the base city for ${name}`}
                            className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline disabled:opacity-50"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
